import { CalendarApi } from '@fullcalendar/core';
import { EventImpl } from '@fullcalendar/core/internal';
import {
  PropsWithChildren,
  useContext,
  Dispatch,
  createContext,
  useReducer,
  useEffect
} from 'react';
import {
  CALENDAR_ACTION_TYPE,
  calendarReducer
} from 'reducers/CalendarReducer';
import axios from 'axios';

export type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';
export type EventModalMode = 'create' | 'edit';

export interface Schedule {
  id: string;
  title: string;
  start: string;
  end?: string;
  description: string;
  className: string;
}

export interface CalendarEvent {
  title: string;
  start: string | Date;
  end?: string | Date;
  description: string;
  className?: string;
  location?: Array<number>;
  organizer?: string;
  schedules?: Schedule[];
  allDay?: boolean;
  url?: string;
  eventType?: string;
  users?: Array<number>;
  takecar?: string;
  leavecar?: string;
  canChangeTime?: 'Y' | 'N';
  //for trips
  status?: string;
  carPlate?: string;
  passenger?: string;
  outsideUsers?: number;
  duration?: number;
  maxPassengers?: number;
}
export interface Events extends CalendarEvent {
  id: string;
}
export interface Trips extends CalendarEvent {
  id: number;
}

export interface Trip {
  id: number | string;
  grupo?: number | string;
  text?: string;
  companiesList?: string;
  listPassenger?: string;
  outsideUsers?: number;
  passenger?: string;
  originalStart?: Date;
  originalEnd?: Date;
  takeCar?: 'Y' | 'N';
  leaveCar?: 'Y' | 'N';
  canChangeTime?: 'Y' | 'N';
}

export interface CalendarState {
  calendarApi: CalendarApi | null;
  title: string;
  view: CalendarView;
  selectedEvent: EventImpl | null;
  openNewEventModal: boolean;
  newEventModalMode: EventModalMode;
  updatingTrip: Trip | null;
  selectedStartDate: Date | string;
  selectedEndDate: Date | string;
  events: Events[];
  trips: Trips[]; // for trips
  isLoadingEvents: boolean;
  isLoadingTrips: boolean; // for trips
  eventsError: string | null;
  tripsError: string | null; // for trips
}

// export interface Schedule {
//   id: string;
//   title: string;
//   start: string;
//   end?: string;
//   description: string;
//   className: string;
// }

// export interface CalendarEvent {
//   id: string;
//   title: string;
//   start: string | Date;
//   end?: string | Date;
//   description: string;
//   className?: string;
//   location?: Array<number> | undefined;
//   organizer?: string;
//   schedules?: Schedule[];
//   allDay?: boolean;
//   url?: string;
//   eventType?: string;
//   users?: Array<number>;
//   takecar?: string;
//   leavecar?: string;
// }

interface CalendarContextInterface extends CalendarState {
  calendarDispatch: Dispatch<CALENDAR_ACTION_TYPE>;
  refreshEvents: () => void;
  refreshTrips: () => void; // for trips
  // for trip update:
  // updateTripStatus: (
  //   tripId: string,
  //   newStatus: 'pending' | 'confirmed' | 'refused',
  //   carPlate?: string
  // ) => Promise<{ success: boolean; error?: string }>;
  bulkUpdateTrips: (
    updates: Array<{ tripId: number; status: string; carPlate?: string }>
  ) => Promise<{ success: boolean; error?: string }>;
}

export const CalendarContext = createContext({} as CalendarContextInterface);

const apiClient = axios.create({
  baseURL: 'https://wsdigital.ctcp.pt/gcViagems',
  // baseURL: 'http://localhost:3400/gcViagems',
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
/*apiClient.interceptors.request.use(
  config => {
    // console.log('Interceptor request config:', config);
    // Add any additional headers if needed
    // Note: httpOnly cookies are automatically included
    return config;
  },
  error => {
    // console.log('Interceptor request error:', error);
    return Promise.reject(error);
  }
);*/

// Response interceptor for handling authentication errors
apiClient.interceptors.response.use(
  response => {
    // console.log('Interceptor response:', response);
    return response;
  },
  error => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      // console.log('401 Interceptor response:', error);
    }
    if (error.response?.status === 404) {
      // Token expired or invalid
      // console.log('404 Interceptor response:', error);
      // Dispatch logout action
    }
    if (error.response?.status === 500) {
      // Token expired or invalid
      // console.log('500 Interceptor response:', error);
      // Dispatch logout action
    }
    // console.log('Interceptor response:', error);

    return Promise.reject(error);
  }
);

interface CalendarProviderProps extends PropsWithChildren {
  loadTripsOnMount?: boolean;
}

const CalendarProvider = ({
  children,
  loadTripsOnMount = true
}: CalendarProviderProps) => {
  const initialState: CalendarState = {
    calendarApi: null,
    title: '',
    view: 'dayGridMonth',
    selectedEvent: null,
    openNewEventModal: false,
    newEventModalMode: 'create',
    selectedStartDate: '',
    selectedEndDate: '',
    events: [],
    trips: [],
    updatingTrip: null,
    isLoadingEvents: false,
    isLoadingTrips: false,
    eventsError: null,
    tripsError: null
  };

  const [calendarState, calendarDispatch] = useReducer(
    calendarReducer,
    initialState
  );

  const fetchEvents = async () => {
    calendarDispatch({
      type: 'SET_CALENDAR_STATE',
      payload: { isLoadingEvents: true, eventsError: null }
    });

    try {
      const response = await apiClient.post('/allUserRequests', {
        withCredentials: true
      });

      // Validate that response.data is an array
      const eventsData = Array.isArray(response.data) ? response.data : [];

      calendarDispatch({
        type: 'SET_CALENDAR_STATE',
        payload: { events: eventsData, isLoadingEvents: false }
      });
    } catch (error) {
      calendarDispatch({
        type: 'SET_CALENDAR_STATE',
        payload: {
          events: [],
          isLoadingEvents: false,
          eventsError: 'Failed to load events'
        }
      });
    }
  };

  const fetchTrips = async () => {
    calendarDispatch({
      type: 'SET_TRIPS',
      payload: { isLoadingTrips: true, tripsError: null }
    });

    try {
      const response = await apiClient.post('/allTrips', {
        withCredentials: true
      });

      // Validate that response.data is an array
      const tripsData = Array.isArray(response.data) ? response.data : [];

      calendarDispatch({
        type: 'SET_TRIPS',
        payload: { trips: tripsData, isLoadingTrips: false }
      });
    } catch (error) {
      calendarDispatch({
        type: 'SET_TRIPS',
        payload: {
          trips: [],
          isLoadingTrips: false,
          tripsError: 'Failed to load trips'
        }
      });
    }
  };

  // const updateTripStatus = async (
  //   tripId: string,
  //   newStatus: 'Pendente' | 'Aprovado' | 'Recusado',
  //   carPlate?: string
  // ): Promise<{ success: boolean; error?: string }> => {
  //   // Store original state for rollback
  //   const originalTrips = [...calendarState.trips];

  //   try {
  //     // OPTIMISTIC UPDATE - Update UI immediately
  //     const updatedTrips = calendarState.trips.map(trip =>
  //       trip.id === tripId
  //         ? {
  //             ...trip,
  //             status: newStatus,
  //             ...(carPlate && { carPlate })
  //           }
  //         : trip
  //     );

  //     // Update UI state
  //     calendarDispatch({
  //       type: 'SET_TRIPS',
  //       payload: { trips: updatedTrips }
  //     });

  //     // BACKEND VALIDATION - Server validates and persists
  //     const response = await apiClient.put(`/updateTrip/${tripId}`, {
  //       status: newStatus,
  //       ...(carPlate && { carPlate })
  //     });

  //     if (!response.data.success) {
  //       throw new Error(response.data.error || 'Update failed');
  //     }

  //     // Refresh to ensure sync with server
  //     await Promise.all([fetchTrips(), fetchEvents()]);

  //     return { success: true };
  //   } catch (error) {
  //     console.error('Failed to update trip status:', error);

  //     // ROLLBACK on error
  //     calendarDispatch({
  //       type: 'SET_TRIPS',
  //       payload: { trips: originalTrips }
  //     });

  //     return {
  //       success: false
  //     };
  //   }
  // };

  /**
   * Bulk update multiple trips (for "Confirm All" functionality)
   * More efficient than individual updates
   */
  const bulkUpdateTrips = async (
    updates: Array<{ tripId: number; status: string; carPlate?: string }>
  ): Promise<{ success: boolean; error?: string }> => {
    const originalTrips = [...calendarState.trips];
    try {
      // Optimistic update
      const tripUpdateMap = new Map(updates.map(u => [u.tripId, u]));

      const updatedTrips = calendarState.trips.map(trip => {
        const update = tripUpdateMap.get(trip.id);
        return update
          ? {
              ...trip,
              status: update.status,
              carPlate: update.carPlate
            }
          : trip;
      });

      calendarDispatch({
        type: 'SET_TRIPS',
        payload: { trips: updatedTrips }
      });

      // Backend bulk update
      const response = await apiClient.post(
        '/bulkUpdateRequests',
        {
          updates
        },
        {
          withCredentials: true
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Bulk update failed');
      }

      // Refresh from server
      await Promise.all([fetchTrips(), fetchEvents()]);

      return { success: true };
    } catch (error) {
      console.error('Failed to bulk update trips:', error);

      // Rollback
      calendarDispatch({
        type: 'SET_TRIPS',
        payload: { trips: originalTrips }
      });

      return {
        success: false
      };
    }
  };

  // Fetch events on mount
  useEffect(() => {
    fetchEvents();
    if (loadTripsOnMount) {
      fetchTrips();
    }
  }, [loadTripsOnMount]);

  // Function to manually refresh events
  const refreshEvents = () => {
    fetchTrips();
    fetchEvents();
  };

  const refreshTrips = () => {
    fetchTrips();
  };

  return (
    <CalendarContext.Provider
      value={{
        ...calendarState,
        calendarDispatch,
        refreshEvents,
        refreshTrips,
        // updateTripStatus,
        bulkUpdateTrips
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = () => useContext(CalendarContext);

export default CalendarProvider;
